import styled from "styled-components/macro";

import { HOUSEHOLD_DB_VIDEO } from "Flocknote/GlobalComponents/Modal/contentKeys";
import {
  useCanHouseholdDatabase,
  useHasFNC,
  useIsBillingAdmin,
  useIsSuperAdmin,
  useModal,
} from "Flocknote/stateHooks";

import FnrTypography from "FnrTypography";
import ActivateFNPButton from "Giving/Marketing/ActivateFNPButton";

import FnrButton3 from "UI/FnrButton3";
import FnrIcon from "UI/FnrIcon";
import { colors } from "UI/colors";

import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";

import blueSwoop from "./blue-swoop.webp";
import lewis from "./lewis.webp";

export default function HouseholdDBSplash() {
  const isBillingAdmin = useIsBillingAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const hasFNC = useHasFNC();
  const [, { openModal }] = useModal();
  const [canHouseholdDB] = useCanHouseholdDatabase();
  const [isClosedHasFNC, setIsClosedHasFNC] = useLocalStorage(
    "householdDBHasFNCSplashClosed",
    false
  );
  const [isClosedNonFNC, setIsClosedNonFNC] = useLocalStorage(
    "householdDBNonFNCSplashClosed",
    false
  );

  const handleClose = () => {
    if (hasFNC) {
      setIsClosedHasFNC(true);
    } else {
      setIsClosedNonFNC(true);
    }
  };

  const videoUrl = hasFNC
    ? "https://player.vimeo.com/video/1056464239?h=96c52c3c98&autoplay=1"
    : "https://player.vimeo.com/video/1053565905?autoplay=1";

  const handleVideoClick = () => {
    openModal(HOUSEHOLD_DB_VIDEO, { videoUrl });
  };

  const getBodyContent = () => {
    return hasFNC ? (
      <>
        Your <em>Flocknote Complete</em> plan now includes a robust household
        database, powerful insights & reporting, advanced giving tracking, & so
        much more!
      </>
    ) : (
      <>
        Upgrade to <em>Flocknote Complete</em> for a robust household database,
        powerful insights & reporting, advanced giving tracking, & so much more!
      </>
    );
  };

  const shouldHideSplash = () => {
    if (!isSuperAdmin) return true; // must be a super admin to see the splash
    if (!canHouseholdDB) return true; // must have access to household database feature
    if (hasFNC && isClosedHasFNC) return true; // they have the feature and have closed the splash
    if (!hasFNC && isClosedNonFNC) return true; // they don't have the feature and have closed the splash
  };

  if (shouldHideSplash()) {
    return null;
  }

  return (
    <Wrapper>
      <Stars className="fas fa-sparkles" />

      <CloseButton onClick={handleClose}>
        <span className="far fa-times" />
      </CloseButton>

      <Content>
        <LeadIn type="header" fontSize="md">
          {hasFNC ? "Introducing" : "Say hello to a"}
        </LeadIn>
        <Title type="subheader" fontSize="xl" italic bold>
          {hasFNC ? "Your Household Database" : "Powerful Household Database"}
        </Title>
        <Body type="copy">{getBodyContent()}</Body>
      </Content>

      <BottomContent>
        <Buttons>
          {videoUrl ? (
            <FnrButton3 color="blue" onClick={handleVideoClick}>
              <FnrIcon fas fa-play-circle style={{ marginRight: 8 }} />
              Watch Video
            </FnrButton3>
          ) : null}

          {hasFNC ? (
            <>
              <a href="/householddatabase">
                <FnrButton3 color="blue" fullWidth>
                  Explore Your Database
                </FnrButton3>
              </a>

              <LearnMore>
                <a
                  href="https://flocknote.com/webinar/#people"
                  target="_blank"
                  rel="noreferrer"
                >
                  Get free training!
                </a>
              </LearnMore>
            </>
          ) : (
            <>
              {isSuperAdmin && isBillingAdmin && (
                <ActivateFNPButton size="medium" fullWidth={true}>
                  Upgrade Today
                </ActivateFNPButton>
              )}

              <LearnMore>
                <a
                  href="https://flocknote.com/complete"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more about Complete
                </a>
              </LearnMore>
            </>
          )}
        </Buttons>
      </BottomContent>

      <LewisImg
        src={lewis}
        alt="Adult and child with Flocknote shirts"
        $hasFnc={hasFNC}
      />
      <BlueSwoop src={blueSwoop} />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  padding: 20px 20px 20px 64px;
  border-radius: 12px;
  background-color: white;
  box-shadow: 1px 2px 6px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  margin-bottom: 20px;
`;

const Stars = styled.span`
  position: absolute;
  top: 20px;
  left: 16px;
  color: ${colors.fnYellow};
  font-size: 36px;
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 20;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: #ddddd9;
  font-size: 24px;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.fnBlue};
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const LeadIn = styled(FnrTypography)`
  font-weight: bold;
  font-size: 1rem;
`;

const Title = styled(FnrTypography)``;

const Body = styled(FnrTypography)`
  line-height: 1.4;
`;

const BottomContent = styled.div`
  position: relative;
  z-index: 10;
  margin-top: 2rem;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  margin-left: 200px;
  min-height: 110px;

  @media (max-width: 500px) {
    margin-left: 0;
  }

  a,
  a:hover {
    text-decoration: none;
  }

  > button {
    width: 100%;
  }
`;

const LearnMore = styled.div`
  text-align: center;

  a {
    text-decoration: underline !important;
    font-size: 13px;
    color: ${colors.fnGray800};
  }
`;

const LewisImg = styled.img`
  z-index: 1;
  position: absolute;
  bottom: 0;
  left: 0;
  height: 165px;
  /* height: ${({ $hasFnc }) => ($hasFnc ? "140px" : "165px")}; */

  @media (max-width: 500px) {
    display: none;
  }
`;

const BlueSwoop = styled.img`
  z-index: 0;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 300px;
`;